/**
 * A calendar day without a time, as the API speaks it: `YYYY-MM-DD`.
 *
 * Taken apart into numbers by hand: `new Date('2026-03-01')` is UTC midnight,
 * which is the day before anywhere west of Greenwich.
 */
export type CalendarDay = { year: number; month: number; day: number }

const pad = (n: number, width = 2) => String(n).padStart(width, '0')

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/

export const isoOf = ({ year, month, day }: CalendarDay): string =>
  `${pad(year, 4)}-${pad(month)}-${pad(day)}`

/** The day a bare ISO day names; null for anything else, `2026-02-31`
 *  included. */
export const dayOf = (iso: string | null | undefined): CalendarDay | null => {
  const match = iso?.match(ISO_DAY)
  if (!match) return null
  const day = { year: +match[1]!, month: +match[2]!, day: +match[3]! }
  return isRealDay(day) ? day : null
}

export const daysInMonth = (year: number, month: number): number =>
  new Date(year, month, 0).getDate()

const isRealDay = ({ year, month, day }: CalendarDay): boolean =>
  year >= 1000 &&
  month >= 1 &&
  month <= 12 &&
  day >= 1 &&
  day <= daysInMonth(year, month)

/** The local calendar day of a moment. */
export const calendarDayOf = (date: Date): CalendarDay => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate(),
})

export const todayIso = (): string => isoOf(calendarDayOf(new Date()))

/**
 * The ISO day a stored date falls on where the reader is, for a field that
 * edits it; null for none. A bare ISO day passes through untouched.
 */
export const isoDayOf = (value: string | null | undefined): string | null => {
  if (!value) return null
  if (dayOf(value)) return value
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : isoOf(calendarDayOf(date))
}

/** A two-digit year as the nearest one that is at most twenty years ahead:
 *  in 2026, `30` is 2030 and `99` is 1999. */
const twoDigitYear = (yy: number): number => {
  const now = new Date().getFullYear()
  const year = now - (now % 100) + yy
  return year > now + 20 ? year - 100 : year
}

/**
 * What an operator typed, as an ISO day: `1.3.2026`, `01.03.26`, `1.3.` for
 * this year, or an ISO day pasted from elsewhere. Null for anything that is not a real day.
 */
export function parseGermanDay(text: string): string | null {
  const trimmed = text.trim()
  if (dayOf(trimmed)) return trimmed
  const [, day, month, year] =
    trimmed.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})?$/) ?? []
  if (!day || !month) return null
  const parsed = {
    year:
      year == null
        ? new Date().getFullYear()
        : year.length === 2
          ? twoDigitYear(+year)
          : +year,
    month: +month,
    day: +day,
  }
  return isRealDay(parsed) ? isoOf(parsed) : null
}
